import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../../context/WalletContext';
import { useContract } from '../../context/ContractContext';
import PageContainer from '../../components/layout/PageContainer/PageContainer';
import { useAdmin } from './useAdmin';
import AdminOverview from './AdminOverview';
import AdminUsers from './AdminUsers';
import AdminRegister from './AdminRegister';
import AdminSettings from './AdminSettings';
import AdminWallets from './AdminWallets';
import AdminOwner from './AdminOwner';
import AdminWiring from './AdminWiring';
import { checkWiring } from './wiring';
import './Admin.css';

// ownerOnly tabs are never shown to the admin wallet
const ALL_TABS = [
  { key: 'overview', label: 'Overview', Component: AdminOverview },
  { key: 'wiring', label: 'Wiring', Component: AdminWiring, ownerOnly: true },
  { key: 'users', label: 'Users', Component: AdminUsers },
  { key: 'register', label: 'Register', Component: AdminRegister },
  { key: 'settings', label: 'Settings', Component: AdminSettings },
  { key: 'wallets', label: 'Wallets', Component: AdminWallets },
  { key: 'owner', label: 'Owner', Component: AdminOwner, ownerOnly: true }
];

const Admin = () => {
  const navigate = useNavigate();
  const { isConnected, isInitializing } = useWallet();
  const { isCheckingRegistration, isRegistered, isAdmin, isOwner } = useContract();
  const admin = useAdmin();
  const [tab, setTab] = useState('overview');
  const [wiring, setWiring] = useState(null);

  const runWiringCheck = async () => {
    try {
      setWiring(await checkWiring(admin.provider, admin.read.storage));
    } catch (err) {
      console.error('Wiring check error:', err);
    }
  };

  // Wiring is an owner-only concern
  useEffect(() => {
    if (isOwner) runWiringCheck();
    else setWiring(null);
  }, [isOwner]);

  // Guard: only owner / admin may stay on this page
  useEffect(() => {
    if (isInitializing || isCheckingRegistration) return;
    if (!isConnected) {
      navigate('/');
    } else if (!isAdmin) {
      navigate(isRegistered ? '/dashboard' : '/register');
    }
  }, [isConnected, isAdmin, isRegistered, isInitializing, isCheckingRegistration, navigate]);

  const tabs = ALL_TABS.filter((t) => isOwner || !t.ownerOnly);

  // If role changes (e.g. ownership transferred), fall back to a tab that still exists
  useEffect(() => {
    if (!tabs.some((t) => t.key === tab)) setTab('overview');
  }, [isOwner]);

  if (!isAdmin) return null;

  const Active = (tabs.find((t) => t.key === tab) || tabs[0]).Component;

  return (
    <PageContainer title="Admin Panel">
      <div className="admin-page">
        <div className="admin-role">
          <span className={`admin-role-badge ${isOwner ? 'owner' : ''}`}>
            {isOwner ? 'Owner' : 'Admin'}
          </span>
        </div>

        <div className="admin-tabs">
          {tabs.map((t) => (
            <button
              key={t.key}
              className={`admin-tab ${tab === t.key ? 'active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {isOwner && wiring && !wiring.allOk && tab !== 'wiring' && (
          <button className="wiring-banner" onClick={() => setTab('wiring')}>
            <span className="wiring-banner-icon">!</span>
            <span>
              Contracts are not wired correctly - user actions will fail.
              <strong> Open Wiring →</strong>
            </span>
          </button>
        )}

        <div className="admin-content">
          <Active key={tab} admin={admin} wiring={wiring} onRecheck={runWiringCheck} />
        </div>
      </div>
    </PageContainer>
  );
};

export default Admin;
